import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { createCustomer, listCustomers } from '@/api/customers';
import { apiFetch, ApiError } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '../auth/AuthContext';
import type { CustomerOut, AccountStatus } from '@/api/types';

interface MeResponse {
  user_type: 'practitioner' | 'customer';
}

export function CustomersPage() {
  const { isSignedIn, userKey, getIdToken } = useAuth();

  const me = useQuery({
    queryKey: ['me', userKey],
    enabled: isSignedIn,
    queryFn: async () => apiFetch<MeResponse>('/api/me', { idToken: (await getIdToken() ?? '') }),
  });

  const q = useQuery({
    queryKey: ['customers', userKey],
    enabled: isSignedIn,
    queryFn: async () => listCustomers((await getIdToken() ?? '')),
  });

  if (!isSignedIn) return <SignedOutCard />;
  if (q.isPending) return <p className="text-brand-slate">Loading customers…</p>;
  if (q.isError) {
    console.error('listCustomers failed:', q.error);
    return (
      <Card className="max-w-xl border-destructive/30">
        <CardHeader>
          <CardTitle>Couldn't load customers</CardTitle>
          <CardDescription>
            Something went wrong. Try refreshing. Details in the browser console.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const customers = q.data.items;
  const canCreate = me.data?.user_type === 'practitioner';

  return (
    <div className="grid gap-4">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-brand-navy">Customers</h2>
          <p className="mt-1 text-sm text-brand-slate">
            {customers.length} {customers.length === 1 ? 'customer' : 'customers'}
          </p>
        </div>
        {canCreate && <CreateAccountTrigger />}
      </div>
      {customers.length === 0 ? (
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>No customers yet</CardTitle>
            <CardDescription>
              {canCreate
                ? 'Use "New customer" above to create one for a customer.'
                : 'Customers are seeded when an invite is issued or via the admin tooling.'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <CustomersTable customers={customers} />
      )}
    </div>
  );
}

function CustomersTable({ customers }: { customers: CustomerOut[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Slug</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Engagements</TableHead>
          <TableHead className="text-right">Audits</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {customers.map((a) => (
          <CustomersTableRow key={a.id} customer={a} />
        ))}
      </TableBody>
    </Table>
  );
}

function CustomersTableRow({ customer: a }: { customer: CustomerOut }) {
  return (
    <TableRow>
      <TableCell>
        <Link
          to="/customers/$slug"
          params={{ slug: a.slug }}
          className="font-medium text-brand-navy underline-offset-4 hover:underline"
        >
          {a.name}
        </Link>
      </TableCell>
      <TableCell className="font-mono text-xs text-brand-slate">{a.slug}</TableCell>
      <TableCell>
        <AccountStatusBadge status={a.status} />
      </TableCell>
      <TableCell className="text-right font-mono">{a.engagement_count}</TableCell>
      <TableCell className="text-right font-mono">{a.audit_run_count}</TableCell>
    </TableRow>
  );
}

function AccountStatusBadge({ status }: { status: AccountStatus }) {
  if (status === 'none') {
    return <span className="text-xs text-brand-slate">—</span>;
  }
  if (status === 'in_progress') {
    return (
      <Badge className="border-transparent bg-brand-royal text-white hover:bg-brand-royal/90">
        in progress
      </Badge>
    );
  }
  if (status === 'completed') {
    return (
      <Badge className="border-transparent bg-emerald-600 text-white hover:bg-emerald-600/90">
        completed
      </Badge>
    );
  }
  return <Badge variant="outline">reviewing</Badge>;
}

function CreateAccountTrigger() {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <Button type="button" size="sm" onClick={() => setOpen(true)}>
        New customer
      </Button>
    );
  }
  return <CreateAccountForm onClose={() => setOpen(false)} />;
}

function CreateAccountForm({ onClose }: { onClose: () => void }) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');

  const create = useMutation({
    mutationFn: async () =>
      createCustomer(
        { name: name.trim(), slug: slug.trim() || undefined },
        (await getIdToken() ?? ''),
      ),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['customers', userKey] });
      void navigate({ to: '/customers/$slug', params: { slug: created.slug } });
    },
  });

  const submittable = name.trim().length > 0 && !create.isPending;

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle>New customer</CardTitle>
        <CardDescription>
          One customer per customer. The slug is auto-generated from the name; override
          it only if you need a specific URL handle.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!submittable) return;
            create.mutate();
          }}
        >
          <label className="grid gap-1 text-sm">
            <span className="text-brand-slate">Customer name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="NowVertical Group Inc."
              maxLength={128}
              required
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-brand-slate">
              Slug <span className="text-xs">(optional — defaults to a slugified name)</span>
            </span>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="nowvertical-group-inc"
              maxLength={64}
              pattern="^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$"
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            />
          </label>
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" disabled={!submittable}>
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={create.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            {create.isError && (
              <span className="text-xs text-destructive">
                {formatCreateError(create.error)}
              </span>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function formatCreateError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) return 'That slug is already in use.';
    if (err.status === 422) return 'Invalid name or slug.';
    return `HTTP ${err.status}`;
  }
  return 'Create failed — see console.';
}

function SignedOutCard() {
  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Not signed in</CardTitle>
        <CardDescription>
          Go back to{' '}
          <Link to="/" className="text-brand-royal underline-offset-4 hover:underline">
            home
          </Link>{' '}
          to sign in.
        </CardDescription>
      </CardHeader>
    </Card>
  );
}
